import {
  anyContainingPermission,
  useAuthorizationRequired,
} from "portal/components/auth/WithAuthorizationRequired";
import {
  PermissionAction,
  PermissionDomain,
  PermissionResource,
} from "protos/portal/auth";

/**
 * Who may add geometry to a farm: set its boundary, draw an obstacle, place
 * a center pivot. Anyone who may update the farm, which is the rule the
 * portal backend enforces on every farm write (`CanUpdate` in
 * golang/services/portal/farms/auth.go) and what the operator app's field
 * recording uploads with: Carbon staff via `update:farms:all`, farm managers
 * and advanced operators via `update:farms:customer`
 * (terraform/auth0/main/roles.ts).
 *
 * Customers adding or editing geometry take responsibility for the safety
 * of the result; every page this admits shows `FarmEditSafetyWarning`.
 */
export const canAddFarmGeometryPermission = anyContainingPermission(
  PermissionAction.update,
  PermissionResource.farms,
  PermissionDomain.customer
);

export const useCanAddFarmGeometry = (): boolean =>
  useAuthorizationRequired(canAddFarmGeometryPermission);

/**
 * Who may change a farm's existing geometry: zone boundaries, holes,
 * buffers, points, whole zones, an obstacle's passability, and a field's
 * center-pivot links. This is its own permission, `update:farms_geometry`,
 * granted to farm managers (customer) and Carbon staff (all) but not to
 * advanced operators, who keep `update:farms` for adding zones and for the
 * operator app's uploads.
 *
 * The portal backend does not check `farms_geometry`: the operator app
 * re-records and re-uploads existing zones through the same farm endpoint
 * with the operator's own token, so requiring it there would stop advanced
 * operators from fixing a field in the field. This is a portal UI rule.
 */
export const canEditFarmGeometryPermission = anyContainingPermission(
  PermissionAction.update,
  PermissionResource.farms_geometry,
  PermissionDomain.customer
);

export const useCanEditFarmGeometry = (): boolean =>
  useAuthorizationRequired(canEditFarmGeometryPermission);
